import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { deleteAccount, deleteAccountErrorKey, type DeletionReceipt } from '../../lib/accountDeletion';
import { supabase } from '../../lib/supabase';

interface Props {
  /** Typed to confirm. Chosen over "type DELETE" so the caregiver has to
   *  register WHOSE data this is, not just perform a ritual. */
  seniorName: string | null;
}

type Phase = 'idle' | 'confirming' | 'deleting' | 'done';

/**
 * The account-deletion control. Lives at the bottom of My Account, below
 * billing — a destructive action does not belong in the dashboard tile grid
 * beside everyday controls.
 *
 * CEO decisions this screen has to state plainly:
 *   D-A  sensitive data goes immediately; the account shell is held 7 days
 *   D-D  NO REFUND — service runs to the end of the paid period
 */
export const DeleteAccountSection: React.FC<Props> = ({ seniorName }) => {
  const { t, i18n } = useTranslation();
  const [phase, setPhase] = useState<Phase>('idle');
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<DeletionReceipt | null>(null);

  const expected = (seniorName ?? '').trim();
  const matches = expected.length > 0 && typed.trim().toLowerCase() === expected.toLowerCase();

  const handleDelete = async () => {
    setPhase('deleting');
    setError(null);
    const result = await deleteAccount();
    if (!result.ok) {
      setError(t(deleteAccountErrorKey(result)));
      setPhase('confirming');
      return;
    }
    setReceipt(result.receipt);
    setPhase('done');
    // Sign-in is already disabled server-side; drop the local session so the
    // dashboard cannot keep rendering against an account that is being erased.
    await supabase.auth.signOut().catch(() => undefined);
  };

  const periodEnd = receipt?.stripe?.periodEndsAt
    ? new Date(receipt.stripe.periodEndsAt).toLocaleDateString(i18n.language === 'fr' ? 'fr-CA' : 'en-CA',
        { year: 'numeric', month: 'long', day: 'numeric' })
    : null;

  if (phase === 'done') {
    return (
      <div className="bg-white rounded-xl border border-emerald-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <span className="material-symbols-outlined text-emerald-600">check_circle</span>
          {t('deleteAccount.done.title')}
        </h2>
        <p className="text-sm text-gray-700 mb-3">{t('deleteAccount.done.body')}</p>
        {periodEnd && (
          <p className="text-sm text-gray-700 mb-3">
            {t('deleteAccount.done.accessUntil', { date: periodEnd })}
          </p>
        )}
        <p className="text-xs text-gray-500">{t('deleteAccount.done.phoneNote')}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-red-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
        <span className="material-symbols-outlined text-red-600">delete_forever</span>
        {t('deleteAccount.title')}
      </h2>
      <p className="text-sm text-gray-600 mb-4">{t('deleteAccount.subtitle')}</p>

      {phase === 'idle' && (
        <button
          onClick={() => setPhase('confirming')}
          className="px-4 py-2 border border-red-300 text-red-700 text-sm font-semibold rounded-lg hover:bg-red-50 transition-colors"
        >
          {t('deleteAccount.start')}
        </button>
      )}

      {(phase === 'confirming' || phase === 'deleting') && (
        <div className="space-y-4">
          <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 space-y-2">
            <p className="text-sm font-semibold text-red-900">{t('deleteAccount.whatGoes.title')}</p>
            <ul className="text-sm text-red-900 list-disc pl-5 space-y-1">
              <li>{t('deleteAccount.whatGoes.device')}</li>
              <li>{t('deleteAccount.whatGoes.contacts')}</li>
              <li>{t('deleteAccount.whatGoes.health')}</li>
              <li>{t('deleteAccount.whatGoes.location')}</li>
            </ul>
          </div>

          <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 space-y-2">
            <p className="text-sm font-semibold text-gray-900">{t('deleteAccount.whatStays.title')}</p>
            <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1">
              <li>{t('deleteAccount.whatStays.consent')}</li>
              <li>{t('deleteAccount.whatStays.invoices')}</li>
              <li>{t('deleteAccount.whatStays.stopList')}</li>
            </ul>
          </div>

          {/* D-D — no refund, access to the period end. Stated before the
              caregiver confirms, not afterwards. */}
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3">
            <p className="text-sm font-semibold text-amber-900 mb-1">{t('deleteAccount.billing.title')}</p>
            <p className="text-sm text-amber-900">{t('deleteAccount.billing.noRefund')}</p>
          </div>

          <p className="text-xs text-gray-500">{t('deleteAccount.graceWindow')}</p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('deleteAccount.confirmLabel', { name: expected })}
            </label>
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              disabled={phase === 'deleting'}
              autoComplete="off"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:opacity-50"
            />
          </div>

          {error && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5">
              <p className="text-sm text-amber-900">{error}</p>
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              onClick={handleDelete}
              disabled={!matches || phase === 'deleting'}
              className="px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {phase === 'deleting' ? t('deleteAccount.deleting') : t('deleteAccount.confirmButton')}
            </button>
            <button
              onClick={() => { setPhase('idle'); setTyped(''); setError(null); }}
              disabled={phase === 'deleting'}
              className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900 disabled:opacity-50"
            >
              {t('deleteAccount.cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeleteAccountSection;
